import { AuthValidationS } from '@/features/Auth/outbound/share';
import {
  CertificationApiS,
  CertificationConstantS,
  CertificationSchemaS,
  CertificationValidationS,
} from '@/features/Certification/outbound/share';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { AT, T } from '@/libs/translation';
import { replaceBrToSpace } from '@/libs/util/replaceBrToSpace';
import { checkConsecutiveChar, isValidPassword } from '@/libs/validation/password';
import {
  Button,
  Input,
  InputChangeEvent,
  type InputData,
  type InputOption,
  InputTimerEndEvent,
  type InputValidatorOption,
  Label,
  Typo,
} from '@/ui/component/atom';
import { ModalBodyView, Toast } from '@/ui/component/cell';
import { getSharedData } from '@/ui/util';
import { pipe } from '@fxts/core';
import { isAxiosError } from 'axios';
import { html } from 'rune-ts';
import { makeErrorPopup, makePopup } from '@/ui/component';
import klass from './ResetPassword.module.scss';

export interface ResetPasswordViewData {
  email?: {
    data: InputData;
    option?: InputOption;
  };
}

export class ResetPasswordView extends ModalBodyView<ResetPasswordViewData> {
  requested_code = false;
  valid_code = false;
  component: {
    password: Input;
    password_check: Input;
    email: Input;
    code: Input;
    request_code: Button;
  };

  constructor(data: ResetPasswordViewData) {
    super(data);

    const password = new Input(
      {},
      {
        attr: {
          maxlength: 30,
          name: 'password',
          type: 'password',
          placeholder: T('auth.reset_password.new_password_placeholder'),
          required: true,
          autocomplete: 'new-password',
        },
        helper: {
          validators: this.passwordValidOption(),
        },
        delete_pattern: /[^a-zA-Z\d{}[\]/?.,;:|)*~`!^\-_+<>@#$%&\\=('"]/g,
      },
    );

    this.component = {
      email: new Input(data?.email?.data ?? {}, {
        ...data?.email?.option,
        attr: {
          name: 'email',
          type: 'email',
          placeholder: T('auth.reset_password.email_placeholder'),
          required: true,
          autocomplete: 'off',
          ...data?.email?.option?.attr,
        },
      }),
      request_code: new Button(
        {
          label: new Typo({ text: T('auth.reset_password.request_email_verify_code') }, { typo: 'p2', weight: 'bold' }),
        },
        {
          size: 'small',
          disabled: !data?.email?.data.value,
          type: 'button',
          klass: `${klass.button} request_code`,
        },
      ),
      code: new Input(
        {},
        {
          attr: {
            name: 'code',
            type: 'text',
            placeholder: T('auth.find_id.code_placeholder'),
            maxlength: 6,
            required: true,
            inputmode: 'numeric',
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('auth.find_id.code_time_end'),
              },
            ],
          },
          controller: {
            type: 'timer',
            timer: {
              minutes: 8,
              autoplay: false,
            },
          },
          klass: `${klass.code} ${klass.disabled}`,
          delete_pattern: /\D/g,
        },
      ),
      password,
      password_check: new Input(
        {},
        {
          attr: {
            maxlength: 30,
            name: 'password_check',
            type: 'password',
            placeholder: T('auth.reset_password.new_password_check_placeholder'),
            required: true,
            autocomplete: 'new-password',
          },
          helper: {
            type: 'check',
            validators: [
              {
                message: T('auth.reset_password.password_match'),
                validator: (val) => {
                  return val === password.value;
                },
              },
            ],
          },
        },
      ),
    };
  }

  override template() {
    return html`<div class="${klass.container}">
      <div class="${klass.container} ${klass.input_container}">
        ${new Label(
          { label: T('auth.reset_password.email'), input: this.component.email, button: this.component.request_code },
          { required: true },
        )}
        ${this.component.code}
      </div>

      ${new Label({ label: T('auth.reset_password.new_password'), input: this.component.password }, { required: true })}
      ${new Label(
        { label: T('auth.reset_password.new_password_check'), input: this.component.password_check },
        { required: true },
      )}
    </div>`;
  }

  passwordValidOption(): InputValidatorOption[] {
    return [
      {
        message: T('auth.signup.password_valid_1'),
        validator: isValidPassword,
      },
      {
        message: T('auth.signup.password_valid_2'),
        validator: checkConsecutiveChar,
      },
      {
        message: T('auth.signup.password_valid_3'),
        validator: (value) => {
          return this.component?.email.value
            ? AuthValidationS.checkEmailChar(value, this.component.email.value ?? '')
            : true;
        },
      },
    ];
  }

  override onRender() {
    this.component.email.focus();
    this.delegate(InputChangeEvent, Input, this.inputChangeHandler);
    this.delegate(InputTimerEndEvent, Input, this.timerEndHandler);
    this.delegate('click', '.request_code', this.requestCodeHandler);
    this.delegate('paste', Input, (e, input) => {
      setTimeout(() => {
        return this.inputChangeHandler(e, input);
      });
    });
    this.delegate('cut', Input, (e, input) => {
      setTimeout(() => {
        return this.inputChangeHandler(e, input);
      });
    });
  }

  override async confirm() {
    return this.requestResetPassword();
  }

  getResetPasswordData() {
    const email = this.component.email.data.value;
    const password_check = this.component.password_check.data.value;
    const password = this.component.password.data.value;
    const code = this.component.code.data.value;

    return {
      email,
      password_check,
      password,
      code,
    };
  }

  async timerEndHandler() {
    const res = await makePopup({
      title: T('auth.signup.code_timer_done'),
      message: T('auth.signup.code_timer_done_description'),
      ok: T('auth.signup.code_retry'),
      cancel: T('common.cancel'),
    });

    const modal = this.getModal();
    modal.button.confirm.disabled();

    if (res === 'confirm') {
      return this.requestCodeHandler();
    }

    return;
  }
  async inputChangeHandler(_e: Event, input: Input) {
    const email_certification_request_data_validation =
      CertificationSchemaS.CreateEmailCertificationResetPwReqBody.safeParse(this.getResetPasswordData());

    if (email_certification_request_data_validation.success && this.component.request_code.option.disabled) {
      this.component.request_code.option.disabled = false;
      this.component.request_code.redraw();
    }

    if (!email_certification_request_data_validation.success && !this.component.request_code.option.disabled) {
      this.component.request_code.option.disabled = true;
      this.component.request_code.redraw();
    }

    if (input.option.attr?.name === 'code' && this.requested_code && !this.valid_code) {
      await this.requestCodeVerifyHandler();
    }

    if (input.option.attr?.name === 'email') {
      this.component.password.resetValidator();
    }

    if (input.option.attr?.name === 'password') {
      this.component.password_check.resetValidator();
    }

    const reset_password_data_validation = CertificationSchemaS.ResetPwReqBody.safeParse(this.getResetPasswordData());

    const modal = this.getModal();

    if (
      reset_password_data_validation.success &&
      modal.button.confirm.option.disabled &&
      this.valid_code &&
      this.component.password.data.value &&
      this.component.password.isValid() &&
      this.component.password_check.data.value &&
      this.component.password_check.isValid()
    ) {
      modal.button.confirm.option.disabled = false;
      modal.button.confirm.redraw();
    }

    if (
      !modal.button.confirm.option.disabled &&
      (!reset_password_data_validation.success ||
        !this.valid_code ||
        !this.component.password.isValid() ||
        !this.component.password_check.isValid())
    ) {
      modal.button.confirm.option.disabled = true;
      modal.button.confirm.redraw();
      return;
    }
  }
  async requestCodeHandler() {
    if (this.valid_code) return;

    try {
      await pipe(
        this.getResetPasswordData(),
        CertificationValidationS.validateCreateEmailCertificationResetPwReqBody,
        CertificationApiS.postEmailCertificationResetPw,
      );

      if (!this.requested_code) {
        this.requested_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.reset_password.request_email_verify_code_retry') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.option.color = 'secondary';
        this.component.request_code.redraw();

        this.component.code.option.klass = `${klass.code} ${klass.fade_in}`;
        this.component.code.redraw();
      }

      this.component.code.inputBaseView.setValue('');
      this.component.code.focus();
      this.component.code.inputControllerView.restart();

      Toast.open({ label: T('common.send_email_with_certification_code'), close_label: T('common.close') }, {});
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        if (e.response?.data.code === 'USER_003') {
          const res = await makePopup({
            title: e.response?.data.title,
            message: e.response?.data.message,
            ok: T('auth.reset_password.signup'),
            cancel: T('common.cancel'),
          });

          if (res === 'confirm') {
            const { client_id } = getSharedData(this);
            location.href = `/${AT.language}/signup?client_id=${client_id}`;
          }

          return;
        }
        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(e);
    }
  }
  async requestCodeVerifyHandler() {
    try {
      if (this.component.code.data.value?.length === 6) {
        await pipe(
          {
            ...this.getResetPasswordData(),
            certification_type: CertificationConstantS.CERTIFICATION_TYPE.RESET_PW,
          },
          CertificationValidationS.validateVerifyEmailCertificationReqBody,
          CertificationApiS.postVerifyEmailCertification,
        );

        this.valid_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.reset_password.verify_code_valid') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.disabled();
        this.component.email.readonly();

        this.component.code.option.klass = `${klass.code} ${klass.fade_out}`;
        this.component.code.inputControllerView.stop();
        this.component.code.redraw();
      }
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        if (e.response?.data.code === 'CERTIFICATION_004') {
          return this.component.code.changeValidatorOnce({
            message: replaceBrToSpace(e.response?.data?.message ?? T('common.errors.title')),
            validator: () => false,
          });
        } else {
          return makeErrorPopup(e, e.response?.data);
        }
      }

      return makeErrorPopup(e);
    }
  }
  async requestResetPassword() {
    try {
      await pipe(this.getResetPasswordData(), CertificationSchemaS.ResetPwReqBody.parse, CertificationApiS.postResetPw);
      Toast.open({ label: T('auth.reset_password.success_guide'), close_label: T('common.close') }, {});
      return true;
    } catch (e) {
      if (isAxiosError(e)) {
        await makeErrorPopup(e, e.response?.data);
      } else {
        await makeErrorPopup(e, {});
      }

      return false;
    }
  }
}
